import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { isEduDemoWorkspace } from './eduDemo/eduDemoWorkspaces'
import { demoListAttendanceForLearner, demoListAttendanceForClass } from './eduDemo/eduDemoDirectReads'
import type {
  EduAttendance,
  EduAttendanceStatus,
} from '../types/schema'

export type { EduAttendance, EduAttendanceStatus }

export interface RollLearner {
  learnerId: string
  displayName: string
  rollNo: string | null
  frozen: boolean
}

export interface GetEduSessionRollInput {
  workspaceId: string
  seriesId: string
  date: string
}

export interface GetEduSessionRollResult {
  learners: RollLearner[]
  marks: Record<string, EduAttendanceStatus>
  onLeaveLearnerIds?: string[]
  session: {
    status: string
    substituteUid: string | null
    rollTakenAt: unknown
  }
}

export interface MarkAttendanceItem {
  learnerId: string
  status: EduAttendanceStatus
}

export interface MarkEduAttendanceInput {
  workspaceId: string
  seriesId: string
  date: string
  marks: MarkAttendanceItem[]
  reason?: string
}

export interface MarkEduAttendanceResult {
  sessionId: string
  rollCounts: Record<EduAttendanceStatus, number>
  markedCount: number
}

export async function getEduSessionRoll(
  input: GetEduSessionRollInput,
): Promise<GetEduSessionRollResult> {
  return callable<GetEduSessionRollInput, GetEduSessionRollResult>('getEduSessionRoll', input)
}

export async function markEduAttendance(
  input: MarkEduAttendanceInput,
): Promise<MarkEduAttendanceResult> {
  return callable<MarkEduAttendanceInput, MarkEduAttendanceResult>('markEduAttendance', input)
}

export async function listAttendanceForLearner(
  workspaceId: string,
  learnerId: string,
  from: string,
  to: string,
  restrictedUid?: string,
): Promise<EduAttendance[]> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListAttendanceForLearner(workspaceId, learnerId, from, to)
  }

  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('learnerId', '==', learnerId),
  ]

  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }

  constraints.push(
    where('date', '>=', from),
    where('date', '<=', to),
    orderBy('date', 'asc'),
  )

  const q = query(collection(firestoreDb, 'edu_attendance'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<EduAttendance, 'id'>),
  }))
}

export async function listAttendanceForClass(
  workspaceId: string,
  classId: string,
  from: string,
  to: string,
  restrictedUid?: string,
): Promise<EduAttendance[]> {
  if (isEduDemoWorkspace(workspaceId)) {
    return demoListAttendanceForClass(workspaceId, classId, from, to)
  }

  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('classId', '==', classId),
  ]

  if (restrictedUid) {
    constraints.push(where('readAccess', 'array-contains', restrictedUid))
  }

  constraints.push(
    where('date', '>=', from),
    where('date', '<=', to),
    orderBy('date', 'asc'),
  )

  const q = query(collection(firestoreDb, 'edu_attendance'), ...constraints)
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<EduAttendance, 'id'>),
  }))
}
